import React, { createContext, useContext, useState, useEffect } from "react";
import { apiService } from "../services/apiService";

const AuthContext = createContext();

// JWT exp is in seconds; an unreadable token counts as expired.
export const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("userData");

    if (token && userData && !isTokenExpired(token)) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error("Error parsing user data:", error);
        localStorage.removeItem("authToken");
        localStorage.removeItem("userData");
      }
    } else {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiService.login(email, password);
      console.log("Login response:", response);

      const token = response?.token || response?.access_token;
      const userData = response?.user || response;

      if (token) {
        localStorage.setItem("authToken", token);
      }
      if (userData) {
        localStorage.setItem("userData", JSON.stringify(userData));
      }
      setUser(userData);

      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config,
      });

      // Only client errors (4xx) are credential problems; server/network failures stay silent.
      const status = error.response?.status;
      const isCredentialError = status >= 400 && status < 500;
      return {
        success: false,
        error: isCredentialError ? "Username atau password salah" : null,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    // Re-checked on every route render so a token that expires mid-session also redirects.
    isAuthenticated: !!user && !isTokenExpired(localStorage.getItem("authToken") || ""),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
